import {useEffect, useState, useRef} from 'react';
import {Link} from 'react-router';
import {Card, Checkbox, Table, Popover, Radio, Row, Col, message} from 'antd';
import {getBaseColumnSearchProps, getColumnSearchProps} from "../widget/SearchProps";
import {
    getAssignedTasks,
    getOwnedTasks,
    updateTaskAssignedTo,
    getAllTasks,
    getTasksByDepartment,
    updateTaskDueDate, getFollowedTasks
} from "../../api/Api";
import {toUtcDateString} from "../../util/timeutils";
import {TaskStatusWidget} from "./TaskStatusWidget";
import {UserSelectWidget} from "./UserSelectWidget";
import {TaskDueWidget} from "./TaskDueWidget";
import {toMs} from "../../util/timeutils";
import {TaskStatusType} from "../../api/Constants";
import {TaskDueIndicator} from "./TaskDueIndicator";

export const TaskTable = ({refresh, onRefresh, onApiFail, session, department, showVerified=false, taskType="owned"}) => {
    const [tasks, setTasks] = useState([]);
    const [assignMap, setAssignMap] = useState({});
    const [dueCount, setDueCount] = useState({});
    const [verified, setVerified] = useState(false);
    const [filterOption, setFilterOption] = useState(0);
    const [messageApi, contextHolder] = message.useMessage();

    useEffect(() => {
        let taskPromise;
        if (taskType === "owned") {
            taskPromise = getOwnedTasks();
        } else if (taskType === "assigned") {
            taskPromise = getAssignedTasks();
        } else if (taskType === "all") {
            taskPromise = getAllTasks();
        } else if (taskType === "department") {
            taskPromise = getTasksByDepartment(department);
        } else if (taskType === "followed") {
            taskPromise = getFollowedTasks();
        }
        taskPromise.then(r => {
            const result = r.map(line => {
                return {
                    ...line,
                    key: line.id,
                    // patch any null departments
                    department: line.department || "Not Assigned",
                }
            });

            setTasks(result);

            // go through the tasks and set the assignees
            const newMap = {};
            // count by due date
            let dueNow = 0, dueSoon = 0;
            const now = new Date();
            result.forEach(task => {
                if ((task.assignedTo !== undefined) && (task.assignedTo !== null)) {
                    const {assignee} = task;
                    newMap[task.id] = assignee;
                }
                const dueDate = new Date(task.dueDate);
                if ((dueDate - now) <= toMs("2 days")) {
                    dueSoon ++;
                }
                if ((dueDate - now) <= toMs("1 day")) {
                    dueNow ++;
                }
            });
            setAssignMap(newMap);
            setDueCount({dueNow, dueSoon});

        }).catch (e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });
    }, [refresh, onApiFail, taskType, department]);

    const setAssignment = (task) => (user) => {
        // console.log(`set assignment ${task.id} ${JSON.stringify(user)}`)
        // console.log(`${JSON.stringify(assignMap[task.id])}`);
        if ((assignMap[task.id] === undefined) || (assignMap[task.id].id !== user.id)) {
            // set the map
            const newMap = {...assignMap};
            newMap[task.id] = user;
            setAssignMap(newMap);

            updateTaskAssignedTo(task.id, user.id).then(r => {
                // console.log(`task assignTo updated to ${user.id}`);
                if (onRefresh) onRefresh();
            }).catch(e => {
                if ((e.status === 401) && (onApiFail)) {
                    onApiFail();
                }
            })
        }
    }

    const setDueDate = (task) => (dateString) => {
        updateTaskDueDate(task.id, dateString).then(r => {
            messageApi.open({
                type: "success",
                content: "Due Date Updated"
            });
            if (onRefresh) onRefresh();
        }).catch (e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
            messageApi.open({
                type: "error",
                content: "Failed to Update Due Date"
            })
        })
    }

    const onFilterChange = (e) => {
        setFilterOption(e.target.value);
    }


    const searchInput = useRef(null);

    const columns = [
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            render: (val, record, index) => {
                return <Popover content={'View Task'}>
                    <Link to={`/task/${encodeURIComponent(record.id)}`}>{val}</Link>
                </Popover>
            },
            sorter: (a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
            ...getColumnSearchProps('title', searchInput)
        },
        {
            title: 'Created',
            dataIndex: 'timestamp',
            key: 'timestamp',
            sorter: (a, b) => (new Date(a.timestamp))- (new Date(b.timestamp)),
            render: (val, record) => {
                return toUtcDateString(new Date(val));
            }
        }
    ];

    if (taskType !== "owned") {
        columns.push({
            title: 'Created By',
            dataIndex: 'creator',
            key: 'creator',
            render: (val, record, index) => {
                return `${record.creator.firstname} ${record.creator.lastname}`;
            },
            sorter: (a, b) => {
                const aName = `${a.creator.firstname} ${a.creator.lastname}`
                const bName = `${b.creator.firstname} ${b.creator.lastname}`
                return aName.toLowerCase().localeCompare(bName.toLowerCase());
            },
        })
    }
    columns.push(
        {
            title: 'Due by',
            dataIndex: 'dueDate',
            key: 'dueDate',
            sorter: (a, b) => (new Date(a.dueDate))- (new Date(b.dueDate)),
            render: (val, record) => {
                let dueDateChange = taskType === "owned" ? setDueDate(record) : undefined;
                return <TaskDueWidget due={val} onChange={dueDateChange} />
            }
        },
    );
    columns.push(
        {
            title: 'Assigned To',
            dataIndex: 'assignedTo',
            key: 'assignedTo',
            render: (val, record, index) => {
                return <UserSelectWidget user={assignMap[record.id]} altText={`assign to`} onApiFail={onApiFail} setUser={setAssignment(record)} />
            },
            sorter: (a, b) => {
                let aName = "zzzzzzzzzzzzzzzzzzzzzzzzzzz", bName = "zzzzzzzzzzzzzzzzzzzzzzzzzzz";
                if (a.assignee) {
                    aName = `${a.assignee.firstname} ${a.assignee.lastname}`
                }
                if (b.assignee) {
                    bName = `${b.assignee.firstname} ${b.assignee.lastname}`
                }

                return aName.toLowerCase().localeCompare(bName.toLowerCase());
            },
            ...getBaseColumnSearchProps('assignee', searchInput),
            onFilter: (value, record) => {
                if ((record.assignee === null) || (record.assignee === undefined)) {
                    return false;
                }
                const name = `${record.assignee.firstname} ${record.assignee.lastname}`;
                return name.toLowerCase().includes(value.toLowerCase());
            },
        },
    );
    if (taskType !== "department") {
        columns.push(
            {
                title: 'Department',
                dataIndex: 'department',
                key: 'department',
                sorter: (a, b) => {
                    const aDept = a.department;
                    const bDept = b.department;
                    return aDept.toLowerCase().localeCompare(bDept.toLowerCase())
                },
                ...getColumnSearchProps('department', searchInput)
            }
        );
    }
    columns.push(
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (val, record, index) => {
                const {createdBy} = record;
                const allowVerified = session && (session.id === createdBy);
                return <TaskStatusWidget task={record} onRefresh={onRefresh} onApiFail={onApiFail} allowVerified={allowVerified} />
            },
            sorter: (a, b) => (a.status - b.status),
        }
    );

    const tablePagination = {
        defaultPageSize: 50,
        showSizeChanger: true,
        pageSizeOptions: [50, 100, 150, 200]
    }

    const onShowVerified = (e) => {
        setVerified(e.target.checked);
    }

    // filter if we are not displaying verified tasks
    let filteredTasks = tasks;
    if (verified !== true) {
        filteredTasks = tasks.filter(t => t.status !== TaskStatusType.Verified)
    }

    // filter for due date
    if (filterOption === 1) {
        // due today
        filteredTasks = filteredTasks.filter(t => {
            const dueDate = new Date(t.dueDate);
            const now = new Date();
            return (dueDate - now) <= toMs("1 day");
        })
    } else if (filterOption === 2) {
        // due soon
        filteredTasks = filteredTasks.filter(t => {
            const dueDate = new Date(t.dueDate);
            const now = new Date();
            return (dueDate - now) <= toMs("2 days");
        })
    }

    return <div>
        {contextHolder}
        {((taskType !== "owned") || (showVerified === true)) &&
            <Card title={<b>Filter Options</b>} size={`small`} style={{textAlign: 'left', marginBottom: '5px'}}>
                {(showVerified === true) &&
                    <div>
                        <Row style={{margin: '5px', marginBottom: '10px'}}>
                            <Col style={{marginRight: '10px'}}>
                                Show Verified
                            </Col>
                            <Col>
                                <Checkbox onClick={onShowVerified}></Checkbox>
                            </Col>
                        </Row>
                    </div>
                }
                {(taskType !== "owned") &&
                <Radio.Group
                    value={filterOption}
                    onChange={onFilterChange}
                >
                    <div><Radio value={0}>All Tasks</Radio></div>
                    <div>
                        <Row>
                            <Col>
                                <Radio value={1}>Due Today</Radio>
                            </Col>
                            <Col>
                                <TaskDueIndicator alert={1} />
                            </Col>
                            { (dueCount.dueNow > 0) &&
                            <Col style={{marginLeft: '5px'}}>
                                <b>{`${dueCount.dueNow} tasks due today.`}</b>
                            </Col>
                            }
                        </Row>
                    </div>
                    <div>
                        <Row>
                            <Col>
                                <Radio value={2}>Due Soon</Radio>
                            </Col>
                            <Col>
                                <TaskDueIndicator alert={2} />
                            </Col>
                            { (dueCount.dueSoon > 0) &&
                                <Col style={{marginLeft: '5px'}}>
                                    <b>{`${dueCount.dueSoon} tasks due soon.`}</b>
                                </Col>
                            }
                        </Row>
                    </div>
                </Radio.Group>
                }
            </Card>
        }
        <Table dataSource={filteredTasks} columns={columns} pagination={tablePagination}/>
    </div>
}